<template>
  <div class="page" id="credits-buy">
    <div class="card">
      <div class="header">
        <div class="description">
          <h2 class="title">Comprar crédito extra</h2>
          <p class="subtitle">Informe um valor mínimo de R$ 50,00 e máximo de R$ {{ user.plan.price / 100 }},00.</p>
        </div>
      </div>

      <validation-observer
        ref="form"
        v-if="!finished"
        v-slot="{ handleSubmit }"
        class="body -not-finished"
      >
        <form @submit.prevent="handleSubmit(buy)">
          <div class="alert -danger" v-if="errors.unrecognized">
            <template slot="title">Ops!</template>

            <p>Ocorreu um erro ao tentar realizar sua compra, tente novamente.</p>
          </div>

          <div class="bg-primary-100 border border-primary-400 text-primary-700 px-4 py-3 rounded relative mb-4 bg-opacity-25" role="alert">
            <span class="block sm:inline">
              Seu plano será renovado em {{ user.organizationPlan.next_charge_date | dateFormat('DD/MM/YYYY') }}.
            </span>
            <span class="absolute top-0 bottom-0 right-0 px-4 py-3"></span>
          </div>

          <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-3" role="alert"  v-show="paymentErrorMessage">
            <span class="block sm:inline">{{ paymentErrorMessage }}</span>
            <span class="absolute top-0 bottom-0 right-0 px-4 py-3"></span>
          </div>

          <validation-provider rules="required" v-slot="{ errors: validationErrors }" name="creditCard">
            <div class="form-group"  :class="{'-has-error': errors.form.price.length > 0 || validationErrors.length > 0}">
              <label for="credit-card" class="label">Cartão de Crédito</label>
              <select
                name="credit-card"
                id="credit-card"
                class="form-control"
                v-model="form.creditCard"
                required
              >
                <option value="">Selecione o cartão de crédito...</option>
                <option v-for="card in paymentMethods" :value="card.id">{{ card.card.brand.toUpperCase() }} ****{{ card.card.last4 }}</option>
              </select>

              <p class="description" v-if="errors.form.price.length > 0 || validationErrors.length > 0">{{ errors.form.price[0] || validationErrors[0] }}</p>
            </div>
          </validation-provider>

          <validation-provider rules="required|min_price" v-slot="{ errors: validationErrors }" name="valor">
            <div class="form-group" :class="{'-has-error': errors.form.price.length > 0 || validationErrors.length > 0}">
              <label for="price" class="label">Valor</label>

              <money
                id="price"
                name="price"
                class="form-control"
                decimal=""
                thousands=""
                prefix="R$ "
                :precision="0"
                v-model="form.price"
                autocomplete="off"
              />
              <p class="description" v-if="errors.form.price.length > 0 || validationErrors.length > 0">{{ errors.form.price[0] || validationErrors[0] }}</p>
            </div>
          </validation-provider>

          <button class="btn btn-primary -full-width" type="submit">Pagar</button>
        </form>
      </validation-observer>

      <div class="body -finished" v-if="finished">
        <success-checkmark/>

        <h2 class="title">Sua compra está sendo processada</h2>
        <p class="description">
          Seus créditos ficarão disponíveis assim que o pagamento for aprovado.
        </p>
        <div class="actions">
          <button
            @click="newBuy()"
            class="btn btn-primary"
            type="submit"
          >
            Nova Compra
          </button>
        </div>
      </div>

      <loader v-if="loading"/>
    </div>
  </div>
</template>

<script>
import { mask } from 'vue-the-mask';
import { Money } from 'v-money';
import { mapState } from 'vuex';
import currency from '@/support/filters/currency';
import Loader from '@/support/components/Loader.vue';
import SuccessCheckmark from '@/support/components/SuccessCheckmark.vue';
import dateFormat from '@/support/filters/dateFormat';
import {
  ValidationProvider,
  ValidationObserver,
} from 'vee-validate';

export default {
  name: 'Buy',

  data() {
    return {
      loading: false,
      finished: false,
      paymentErrorMessage: '',
      form: {
        price: 50,
        creditCard: '',
      },
      errors: {
        unrecognized: false,
        form: {
          price: [],
        },
      },
    };
  },

  methods: {
    /**
     * Buy the credits.
     *
     * @returns {Promise<void>}
     */
    async buy() {
      this.paymentErrorMessage = '';
      this.loading = true;

      try {
        const response = await this.$store.dispatch('credits/payRecharge', this.form);

        if (response) {
          console.log('Erro: ', response);
        }

        this.finished = true;
      } catch (err) {
        const { data, status } = err.response;

        if (status === 422) {
          this.paymentErrorMessage = data.message;
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * Reset the errors from page.
     */
    resetErrors() {
      this.errors.unrecognized = false;
      this.errors.form.price = [];
    },

    /**
     * Reset the form, errors to make a new buy.
     */
    newBuy() {
      this.resetErrors();
      this.form.price = 50;
      this.form.creditCard = '';
      this.finished = false;
    },
  },

  watch: {
  },

  async mounted() {
    this.loading = true;
    await this.$store.dispatch('credits/paymentMethods');
    this.loading = false;
  },

  computed: {
    ...mapState('credits', {
      lastBuy: 'lastBuy',
    }),
    ...mapState('auth', {
      user: 'user',
    }),
    ...mapState('credits', [
      'paymentMethods',
    ]),
  },

  filters: {
    currency,
    dateFormat,
  },

  directives: {
    mask,
  },

  components: {
    Money,
    Loader,
    SuccessCheckmark,
    ValidationProvider,
    ValidationObserver,
  },
};
</script>

<style lang="scss" scoped>
#credits-buy {
  > .card {
    > .body, .body form {
      > .alert {
        @apply mb-4;
      }

      > .choose-payment-method {
        > .paymentOptions {
          @apply flex;
          @apply flex-wrap;
          @apply gap-4;

          @screen md {
            @apply flex-no-wrap;
          }

          > .option {
            @apply w-full;
            @apply p-4;
            @apply border;
            @apply border-solid;
            @apply border-gray-400;
            @apply rounded;
            @apply text-grey-800;
            @apply font-bold;
            @apply cursor-pointer;

            @screen md {
              @apply w-auto;
              min-width: auto;
            }

            > .form-check-input {
              @apply relative;
              top: 1px;
            }

            > .icon {
              @apply ml-2;
              @apply relative;
              top: 2px;
            }
          }
        }

        &.-has-error {
          > .paymentOptions {
            > .option {
              @apply border-red-500;
            }
          }
        }
      }

      &.-finished {
        > .title {
          @apply text-2xl;
          @apply text-center;
          font-weight: bold;
          font-family: 'Roboto Slab', serif;
        }

        > .description {
          @apply pb-4 px-4;
          @apply text-sm;
          @apply text-center;
        }

        > .actions {
          @apply text-center;
          @apply mt-4;
          > .btn {
            @apply mx-1;
          }
        }
      }
    }
  }
}
</style>
